/*
 * THE OFFICIAL MARKETPLACE WIDGETS — one list, read live by everything that offers them.
 *
 * The marketplace panel draws a card per entry; the assistant's `dashboards.widgets` tool lists
 * the same entries as placeable; a tile placed from either carries the entry's SOURCE inlined
 * (clone-to-own, the same install a person performs with the card's download). Both read this
 * function at the moment they need it, so a widget added here is on the shelf and in the
 * assistant's toolbox in the same build with no second registration — and when a marketplace
 * service exists, this function becomes its client and nothing above it changes.
 *
 * Every reading here is made up. `demo` is the controller id the card configs name; the person
 * (or the assistant) binds real symbols after placing. Nobody's plant is in this file.
 */
import { catalogEntry, type CatalogEntry } from './catalog';
import { ideaEntry } from './ideas';
import type { DemoSeries } from './widget-demo';
import { isMarketplaceWidget, marketplaceItemId } from './installed-widget';

export interface OfficialWidget {
  /** The marketplace item id (`kpi-strip`, `tank`); the assistant names it `marketplace:<id>`. */
  id: string;
  title: string;
  summary: string;
  /** The widget itself: source, config keys, defaults, minimum size. */
  entry: CatalogEntry;
  /** The vanilla config the download hands over — a card's demo bindings on top of the defaults. */
  config: Record<string, unknown>;
  /** How the card previews it: a ticking demo (with its series), or the real component with no data. */
  preview: 'demo' | 'host';
  /** For a demo preview: the config the demo runs with (a built-in's card reads one `preview` symbol). */
  previewConfig: Record<string, unknown>;
  series: DemoSeries[];
  rate: number;
  /** True when the source is one of the app's built-in tiles — a preset of it, not a new widget. */
  builtIn: boolean;
}

/** Every official widget, in shelf order. Built on each call: this is the live pointer. */
export function officialWidgets(): OfficialWidget[] {
  // Idea widgets with made-up, ticking readings: the card shows the widget at work (a spectrum
  // finding its peak, a box plot filling), never a live symbol.
  const D = 'demo';
  const sym = (symbolId: string, label: string, extra: Record<string, unknown> = {}) => ({ controllerId: D, symbolId, label, ...extra });
  const wave = (symbolId: string, base: number, amp: number, period: number, noise = 0, phase = 0): DemoSeries => ({
    symbolId,
    base,
    amp,
    period,
    noise,
    phase,
  });
  const cores6 = ['c1', 'c2', 'c3', 'c4', 'c5', 'c6'];
  const coreWaves = cores6.map((id, i) => wave(id, 30 + i * 8, 12 + i * 2, 14 + i * 3, 3, i / 6));
  const ideas: {
    id: string;
    idea: string;
    title: string;
    summary: string;
    config: Record<string, unknown>;
    series: DemoSeries[];
    rate?: number;
  }[] = [
    {
      id: 'kpi',
      idea: 'kpi',
      title: 'KPI',
      summary: 'One number, its change over the window, min · avg · max, a bar against the average and the history on the chart’s axes.',
      config: { ...sym('oee', 'OEE'), unit: '%', windowSec: 120, decimals: 1, warn: 60, fault: 50, invert: true, target: 85 },
      series: [wave('oee', 78, 6, 40, 1.5)],
      rate: 4,
    },
    {
      id: 'kpi-strip',
      idea: 'kpi-strip',
      title: 'KPI Strip',
      summary: 'The KPI tile, n times side by side: value, change, min · avg · max, the bar against the average and the history.',
      config: {
        windowSec: 120,
        items: [
          sym('s1', 'OEE', { unit: '%', target: 85 }),
          sym('s2', 'Throughput', { unit: ' pcs/h', decimals: 0 }),
          sym('s3', 'Scrap', { unit: '%', warn: 3, fault: 5 }),
        ],
      },
      series: [wave('s1', 78, 6, 40, 1.5), wave('s2', 1240, 60, 25, 15, 0.3), wave('s3', 2.1, 0.6, 30, 0.2)],
      rate: 4,
    },
    {
      id: 'headroom',
      idea: 'headroom',
      title: 'Headroom',
      summary: 'How much of each capacity is still free: used as a track, the free share as the number.',
      config: {
        items: [
          sym('h1', 'Buffer A', { capacity: 400, unit: ' pcs', warn: 0.85 }),
          sym('h2', 'Buffer B', { capacity: 400, unit: ' pcs', warn: 0.85 }),
          sym('h3', 'Air', { capacity: 8, unit: ' bar', warn: 0.9 }),
        ],
      },
      series: [wave('h1', 300, 60, 40, 5), wave('h2', 180, 40, 25, 5, 0.4), wave('h3', 6.4, 0.6, 30, 0.1)],
      rate: 2,
    },
    {
      id: 'lanes',
      idea: 'lanes',
      title: 'Sparkline Lanes',
      summary: 'Many signals at a glance: a sparkline per row on a shared time axis, the value now, the window’s range.',
      config: {
        windowSec: 120,
        items: [
          sym('l1', 'Line speed', { unit: ' m/min' }),
          sym('l2', 'Motor temp', { unit: ' °C', decimals: 0 }),
          sym('l3', 'Tension', { unit: ' N' }),
          sym('l4', 'Vibration', { unit: ' mm/s', decimals: 2 }),
        ],
      },
      series: [wave('l1', 42, 3, 20, 1), wave('l2', 61, 4, 50, 0.6), wave('l3', 120, 15, 12, 4), wave('l4', 1.2, 0.4, 7, 0.15)],
      rate: 4,
    },
    {
      id: 'polar',
      idea: 'polar',
      title: 'Polar Area',
      summary: 'Equal-angle wedges whose radius is the value, on a shared radial scale, with a legend.',
      config: {
        label: 'Downtime by cause',
        unit: ' min',
        max: 60,
        items: [sym('q1', 'Changeover'), sym('q2', 'Jam'), sym('q3', 'Material'), sym('q4', 'Quality'), sym('q5', 'Other')],
      },
      series: [
        wave('q1', 48, 6, 25, 1),
        wave('q2', 30, 10, 15, 2, 0.3),
        wave('q3', 38, 8, 30, 2, 0.6),
        wave('q4', 22, 6, 20, 1),
        wave('q5', 12, 4, 40, 1),
      ],
      rate: 3,
    },
    {
      id: 'histogram',
      idea: 'histogram',
      title: 'Distribution',
      summary: 'How a value has been distributed over the window, its percentiles as coloured rules, the mean and σ.',
      config: { ...sym('ct', 'Cycle time'), unit: ' s', windowSec: 120, bins: 16 },
      series: [wave('ct', 31.5, 1.2, 9, 1.4)],
      rate: 6,
    },
    {
      id: 'correlation-lens',
      idea: 'correlation-lens',
      title: 'Correlation Lens',
      summary: 'The matrix beside the strongest pair scattered with its fit; a small tile shows the pair alone.',
      config: { label: 'Drives', windowSec: 120, symbols: cores6.slice(0, 5).map((id, i) => sym(id, 'M' + (i + 1))) },
      series: [
        { ...wave('c1', 30, 10, 14, 2), walk: 1.5 },
        { ...wave('c2', 32, 9, 14, 2, 0.05), walk: 1.5 },
        { ...wave('c3', 40, 12, 14, 2, 0.5), walk: 2 },
        { ...wave('c4', 25, 8, 9, 3), walk: 1.2 },
        { ...wave('c5', 35, 10, 21, 2, 0.3), walk: 1.5 },
      ],
      rate: 4,
    },
    {
      id: 'bar-stacked',
      idea: 'bar-stacked',
      title: 'Stacked Bar',
      summary: 'Groups of parts, stacked or side by side, upright or lying, on the chart’s axes.',
      config: {
        label: 'Output by line',
        unit: ' t',
        groups: [
          { label: 'Line A', items: [sym('a1', 'good'), sym('a2', 'rework')] },
          { label: 'Line B', items: [sym('b1', 'good'), sym('b2', 'rework')] },
          { label: 'Line C', items: [sym('c1', 'good'), sym('c2', 'rework')] },
        ],
      },
      series: [
        wave('a1', 42, 8, 11, 1),
        wave('a2', 6, 3, 7, 1),
        wave('b1', 35, 10, 13, 1, 0.3),
        wave('b2', 9, 4, 9, 1),
        wave('c1', 50, 6, 17, 1, 0.6),
        wave('c2', 4, 2, 5, 1),
      ],
      rate: 4,
    },
    {
      id: 'doughnut',
      idea: 'doughnut',
      title: 'Doughnut',
      summary: 'Shares of a total as a ring, the largest share in the middle, every wedge named on hover.',
      config: {
        label: 'Energy by area',
        unit: ' kW',
        centre: 'largest',
        items: [sym('e1', 'Presses'), sym('e2', 'Ovens'), sym('e3', 'Conveyors'), sym('e4', 'Lighting')],
      },
      series: [wave('e1', 120, 30, 15, 2), wave('e2', 90, 20, 9, 2, 0.5), wave('e3', 40, 10, 12, 1), wave('e4', 18, 2, 30, 0.5)],
      rate: 4,
    },
    {
      id: 'pie',
      idea: 'pie',
      title: 'Pie',
      summary: 'Shares of a total as a full disc, with a legend and the same name · value · share on hover.',
      config: { label: 'Shift output', unit: '', items: [sym('p1', 'Good'), sym('p2', 'Rework'), sym('p3', 'Scrap')] },
      series: [wave('p1', 880, 40, 20, 5), wave('p2', 60, 20, 11, 3), wave('p3', 22, 8, 7, 2)],
      rate: 4,
    },
    {
      id: 'radar',
      idea: 'radar',
      title: 'Radar',
      summary: 'One value per axis as a polygon, against the window mean; numbered axes with a legend.',
      config: {
        label: 'Cell health',
        windowSec: 120,
        compare: 'window-mean',
        axes: [
          sym('r1', 'Availability', { max: 100, unit: '%' }),
          sym('r2', 'Performance', { max: 100, unit: '%' }),
          sym('r3', 'Quality', { max: 100, unit: '%' }),
          sym('r4', 'Energy', { max: 100, unit: '%' }),
          sym('r5', 'Safety', { max: 100, unit: '%' }),
        ],
      },
      series: [
        wave('r1', 88, 8, 13, 1),
        wave('r2', 74, 12, 17, 2, 0.3),
        wave('r3', 93, 4, 9, 1),
        wave('r4', 66, 10, 21, 2, 0.6),
        wave('r5', 97, 2, 30, 0.5),
      ],
      rate: 3,
    },
    {
      id: 'scatter',
      idea: 'scatter',
      title: 'Scatter',
      summary: 'Two values against each other with a least-squares fit and r; hover a point for both values.',
      config: { x: sym('sx', 'Speed', { unit: 'm/min' }), y: sym('sy', 'Power', { unit: 'kW' }), windowSec: 120, color: 'time' },
      series: [wave('sx', 40, 12, 23, 2), wave('sy', 60, 15, 23, 4, 0.02)],
      rate: 5,
    },
    {
      id: 'stats',
      idea: 'stats',
      title: 'Moving Stats',
      summary: 'The window with its EWMA, the mean ± kσ band, and the points beyond it.',
      config: { ...sym('mv', 'Pressure'), unit: ' bar', windowSec: 90, k: 2, alpha: 0.2 },
      series: [wave('mv', 6.2, 0.4, 25, 0.25)],
      rate: 5,
    },
    {
      id: 'heatmap',
      idea: 'heatmap',
      title: 'Heatmap',
      summary: 'Time on x, one row per signal, colour is the level — with the scale spelled out.',
      config: {
        label: 'Zone temperatures',
        unit: '°C',
        windowSec: 120,
        bucketSec: 4,
        symbols: cores6.map((id, i) => sym(id, 'Zone ' + (i + 1))),
      },
      series: coreWaves,
      rate: 3,
    },
    {
      id: 'boxplot',
      idea: 'boxplot',
      title: 'Box Plot',
      summary: 'Median, quartiles, p5–p95 whiskers and outliers per signal over the window.',
      config: {
        label: 'Cycle time by station',
        unit: ' s',
        windowSec: 120,
        symbols: cores6.slice(0, 4).map((id, i) => sym(id, 'St ' + (i + 1))),
      },
      series: coreWaves.slice(0, 4),
      rate: 5,
    },
    {
      id: 'fft',
      idea: 'fft',
      title: 'Spectrum',
      summary: 'The frequency content of one signal with its dominant period; hover a bar for its frequency and amplitude.',
      config: { ...sym('vib', 'Vibration'), unit: ' mm/s', sampleHz: 4, size: 128 },
      series: [{ ...wave('vib', 2, 1.1, 6, 0.35), amp2: 0.7, period2: 2.3 }],
      rate: 4,
    },
    {
      id: 'deviation',
      idea: 'deviation',
      title: 'Deviation Explainer',
      summary: 'How far a target sits from its baseline, and which drivers moved with it, on one ±3σ axis.',
      config: {
        target: sym('tq', 'Torque', { unit: ' Nm' }),
        drivers: [sym('d1', 'Speed'), sym('d2', 'Feed'), sym('d3', 'Coolant'), sym('d4', 'Tool wear')],
        baselineSec: 90,
        recentSec: 10,
      },
      series: [
        { ...wave('tq', 40, 1.5, 30, 0.6), shift: 5 },
        { ...wave('d1', 1200, 40, 30, 12, 0.02), shift: 110 },
        { ...wave('d2', 0.3, 0.01, 45, 0.006), shift: -0.022 },
        { ...wave('d3', 18, 0.3, 60, 0.2), shift: -0.9 },
        { ...wave('d4', 0.6, 0.03, 30, 0.01, 0.5), shift: 0.06 },
      ],
      rate: 4,
    },
    {
      id: 'anomaly',
      idea: 'anomaly',
      title: 'Anomaly Score',
      summary: 'How unusual the last seconds are against the session baseline, in σ, with the raw value behind it.',
      config: { ...sym('flow', 'Flow'), unit: ' L/min', baselineSec: 90, shortSec: 6, warn: 2, fault: 3 },
      series: [{ ...wave('flow', 120, 4, 40, 3), shift: 14, shiftSec: 8 }],
      rate: 4,
    },
    {
      id: 'bar-time',
      idea: 'bar-time',
      title: 'Time Buckets',
      summary: 'One symbol bucketed over the window: avg, max, min, last, count or sum per bucket, on the chart’s axes.',
      config: { ...sym('rate', 'Throughput'), unit: ' pcs', windowSec: 120, bucketSec: 5, agg: 'sum' },
      series: [wave('rate', 12, 4, 30, 2)],
      rate: 4,
    },
    {
      id: 'correlation',
      idea: 'correlation',
      title: 'Correlation Matrix',
      summary: 'Pearson correlation between signals over the window, centred, each cell explained on hover.',
      config: { label: 'Drives', windowSec: 120, stepMs: 500, symbols: cores6.slice(0, 5).map((id, i) => sym(id, 'M' + (i + 1))) },
      series: [
        { ...wave('c1', 30, 10, 14, 2), walk: 1.5 },
        { ...wave('c2', 32, 9, 14, 2, 0.05), walk: 1.5 },
        { ...wave('c3', 40, 12, 14, 2, 0.5), walk: 2 },
        { ...wave('c4', 25, 8, 9, 3), walk: 1.2 },
        { ...wave('c5', 35, 10, 21, 2, 0.3), walk: 1.5 },
      ],
      rate: 4,
    },
    {
      id: 'xcorr',
      idea: 'xcorr',
      title: 'Cross-Correlation',
      summary: 'Correlation between two signals at every lag above a noise floor, and the pair aligned at the best lag.',
      config: { x: sym('xa', 'Feed'), y: sym('xb', 'Torque'), windowSec: 120, maxLagSec: 12, stepMs: 500 },
      series: [
        { ...wave('xa', 50, 12, 24, 0.8), amp2: 3, period2: 7 },
        { ...wave('xb', 30, 8, 24, 0.6, -0.125), amp2: 2, period2: 7 },
      ],
      rate: 4,
    },
  ];
  // The built-in tiles as presets. `at` is the value the card shows — a static reading so the
  // styling can be judged, never a subscription. (The chart draws its own made-up trace.)
  const rows: { id: string; catalog: string; title: string; summary: string; config: Record<string, unknown>; at?: number }[] = [
    {
      id: 'dial',
      catalog: 'dial',
      title: 'Dial',
      summary: 'An analog needle meter with ticks, scale labels and warn/danger zones.',
      config: { min: 0, max: 100, warn: 70, danger: 90, unit: '%', majorTicks: 5, label: 'Line speed' },
      at: 63,
    },
    {
      id: 'tank',
      catalog: 'fill',
      title: 'Tank',
      summary: 'A vessel that fills, with a live surface and graduations. Amber when low or high, red near overflow.',
      config: { min: 0, max: 100, shape: 'tank', unit: '%', label: 'Buffer tank' },
      at: 58,
    },
    {
      id: 'battery',
      catalog: 'fill',
      title: 'Battery',
      summary: 'A segmented charge level with a terminal; green, amber, red as it drains.',
      config: { min: 0, max: 100, shape: 'battery', unit: '%', label: 'AGV 3' },
      at: 82,
    },
    {
      id: 'meter',
      catalog: 'meter',
      title: 'Meter',
      summary: 'A needle on an arc with tone bands outside it: temperature, rpm, pressure, level. A full or half arc.',
      config: {
        label: 'Oil temperature',
        min: 0,
        max: 120,
        unit: '°C',
        decimals: 0,
        bands: [
          { max: 70, tone: 'green' },
          { max: 95, tone: 'amber' },
          { max: 120, tone: 'red' },
        ],
      },
      at: 68,
    },
    {
      id: 'chart',
      catalog: 'chart',
      title: 'Trend',
      summary:
        'A live time-series chart, one trace or many: each on its own scale with a legend and live values, grid, fill and crosshair, smooth at frame rate.',
      config: {
        windowSec: 120,
        label: 'Flow',
        symbols: [
          { controllerId: 'demo', symbolId: 'flow', label: 'Flow', unit: 'L/min' },
          { controllerId: 'demo', symbolId: 'pressure', label: 'Pressure', unit: 'bar' },
        ],
      },
    },
  ];
  const out: OfficialWidget[] = [];
  for (const r of ideas) {
    const entry = ideaEntry(r.idea);
    if (!entry) continue;
    const config = { ...entry.defaults, ...r.config };
    out.push({
      id: r.id,
      title: r.title,
      summary: r.summary,
      entry,
      config,
      preview: 'demo',
      previewConfig: config,
      series: r.series,
      rate: r.rate ?? 2,
      builtIn: false,
    });
  }
  for (const r of rows) {
    const entry = catalogEntry(r.catalog);
    if (!entry) continue;
    const config = { ...entry.defaults, ...r.config };
    const previewConfig = { ...config, controllerId: 'preview', symbolId: 'preview' };
    const series: DemoSeries[] = r.at === undefined ? [] : [wave('preview', r.at, r.at * 0.06, 25, r.at * 0.01)];
    out.push({
      id: r.id,
      title: r.title,
      summary: r.summary,
      entry,
      config,
      preview: 'demo',
      previewConfig,
      series,
      rate: 4,
      builtIn: true,
    });
  }
  // The native tiles that need no symbol: the note and the model panels. Previewed as the real
  // components with demo content; the download hands over the vanilla config.
  for (const [id, title, summary] of [
    [
      'notifications',
      'Notifications',
      'Every watched point outside its limits, oldest first, with its value and how long it has been out; an all-clear line otherwise.',
    ],
    ['note', 'Note', 'A rich-text note: headings, lists, a checklist you can tick on the board, code and highlights.'],
    ['model-commands', 'Model commands', "One model's declared commands as buttons — the model page's Commands panel as a tile."],
    [
      'model-statechart',
      'Model statechart',
      "One model's live statechart with the active state lit — the model page's Statechart panel as a tile.",
    ],
  ] as const) {
    const entry = catalogEntry(id);
    if (!entry) continue;
    // The notifications card shows three watched points, one in each state; the rest need no config.
    const config =
      id === 'notifications'
        ? {
            items: [
              { controllerId: 'demo', symbolId: 'oil', label: 'Oil temperature', high: 95, margin: 10, unit: '°C' },
              { controllerId: 'demo', symbolId: 'load', label: 'Load 1m', high: 4 },
              { controllerId: 'demo', symbolId: 'ram', label: 'RAM free', low: 8, unit: 'GB' },
            ],
          }
        : { ...entry.defaults };
    out.push({
      id,
      title,
      summary,
      entry,
      config,
      preview: 'host',
      previewConfig: id === 'notifications' ? config : {},
      series: [],
      rate: 0,
      builtIn: true,
    });
  }
  return out;
}

/** One official widget by its item id, or by its assistant-facing `marketplace:<id>` name. */
export function officialWidget(id: string): OfficialWidget | undefined {
  const item = isMarketplaceWidget(id) ? marketplaceItemId(id) : id;
  return officialWidgets().find((w) => w.id === item);
}

/**
 * The catalogue entry the assistant places an official widget with: the widget's own source, keys,
 * defaults and size under its `marketplace:<id>` name. A preset of a built-in resolves to the
 * built-in's entry with the preset's config folded into the defaults, so "tank" places a fill
 * shaped as a tank.
 */
export function officialWidgetEntry(id: string): CatalogEntry | undefined {
  const w = officialWidget(id);
  if (!w) return undefined;
  const defaults = w.builtIn ? { ...w.entry.defaults, ...w.config, controllerId: undefined, symbolId: undefined } : w.entry.defaults;
  return {
    ...w.entry,
    id: `marketplace:${w.id}`,
    title: w.title,
    summary: w.summary,
    defaults: Object.fromEntries(Object.entries(defaults).filter(([, v]) => v !== undefined)),
  };
}

/** The official widget a stored tile's source came from — how a board reads back its widget names. */
export function officialForSource(source: string): CatalogEntry | undefined {
  const w = officialWidgets().find((x) => !x.builtIn && x.entry.source === source);
  return w ? officialWidgetEntry(w.id) : undefined;
}
